'use client'
import { useEffect, useState, useRef } from 'react'
import { ArticleTOC } from 'components/ArticleTOC'
import { TypographyLongForm } from 'components/Contentful/TypographyLongForm'
import { BLOCKS } from '@contentful/rich-text-types'
import { removeSpecialCharacters } from 'utils/shared'

function StateContentWithTOC({ content }) {
  const [tableOfContents, setTableOfContents] = useState([])
  const [activeSection, setActiveSection] = useState()
  const articleContainerRef = useRef(null)
  const refs = useRef({})

  // Generate table of contents from H2 elements
  useEffect(() => {
    const articles = []
    const richTextContent = content?.json?.content ?? content?.content

    if (richTextContent) {
      for (let i = 0; i < richTextContent.length; i++) {
        const element = richTextContent[i]
        if (element.nodeType === BLOCKS.HEADING_2) {
          const sectionContent = element?.content?.[0]?.value ?? ''
          articles.push({
            text: sectionContent,
            id: removeSpecialCharacters(
              `${BLOCKS.HEADING_2}-${sectionContent.slice(0, 50).replaceAll(' ', '-')}`
            ),
          })
        }
      }
    }

    setTableOfContents(articles)
  }, [content])

  // Track active section via IntersectionObserver
  useEffect(() => {
    if (!tableOfContents?.length) return

    setActiveSection(tableOfContents[0]?.id)

    const container = articleContainerRef.current
    tableOfContents.forEach(({ id }) => {
      refs.current[id] = container?.querySelector(`#${CSS.escape(id)}`) ?? null
    })

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id)
        })
      },
      {
        root: null,
        rootMargin: '-30% 0px -30% 0px',
        threshold: 0,
      }
    )

    tableOfContents.forEach(({ id }) => {
      if (refs.current[id]) observer.observe(refs.current[id])
    })

    const currentRefs = refs.current

    return () => {
      tableOfContents.forEach(({ id }) => {
        if (currentRefs[id]) observer.unobserve(currentRefs[id])
      })
    }
  }, [tableOfContents])

  const showTOC = tableOfContents?.length >= 1

  if (!content) {
    return null
  }

  return (
    <section className="mx-auto max-w-7xl px-5 py-12 md:px-6 md:py-16" ref={articleContainerRef}>
      <div className={`flex flex-col ${showTOC ? 'lg:flex-row lg:gap-8' : ''}`}>
        {showTOC && (
          <div className="hidden lg:block lg:w-1/4">
            <div className="sticky top-24">
              <ArticleTOC
                content={tableOfContents}
                withTitle={true}
                variantItem="labelSm"
                activeSection={activeSection}
              />
            </div>
          </div>
        )}

        <div className={showTOC ? 'w-full lg:w-3/4' : 'w-full'}>
          <TypographyLongForm
            content={content}
            variantOverride={{
              [BLOCKS.HEADING_2]: 'headingMd',
              [BLOCKS.PARAGRAPH]: 'bodyMd',
            }}
          />
        </div>
      </div>
    </section>
  )
}

export { StateContentWithTOC }
export default StateContentWithTOC
